/**
 * Merchant Redemption History Page
 *
 * Comprehensive view of all coupon redemptions with filtering and statistics.
 * Provides historical data analysis and export capabilities for merchant staff and admins.
 *
 * Features:
 * - Redemption history with pagination
 * - Date range filtering
 * - Statistics and analytics
 * - Responsive design with mobile support
 * - Export functionality (future enhancement)
 */

import React from 'react';
import { useRedemptionHistory } from '../../hooks-v2/aggregator';
import { useCurrentMerchant } from '@/hooks-v2/context';
import { TimestampService, formatMoney } from '@zlinkx/shared-v2';
import { useTranslation } from '@/i18n';
import { Clock, RefreshCw, TrendingUp, DollarSign, Receipt, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';

const RedemptionHistory: React.FC = () => {
	const { t } = useTranslation();

	const { selectedMerchant, selectedMerchantId, loading: isLoadingMerchants } = useCurrentMerchant();

	// Redemption history hook
	const redemptionHistory = useRedemptionHistory({
		merchantId: selectedMerchantId ?? 0,
		enabled: selectedMerchantId !== null,
	});
	const redemptions = redemptionHistory.data.redemptions;
	const isLoadingHistory = redemptionHistory.isLoading;
	const historyError = redemptionHistory.error;
	const stats = redemptionHistory.computed.statistics;

	// Format currency for display (expects cents)
	const formatCurrency = (cents: number): string => {
		return formatMoney(cents);
	};

	// Format date for display
	const formatDate = (timestamp: string | number): string => {
		const date =
			typeof timestamp === 'string'
				? TimestampService.parseDateTimeInput(timestamp)
				: (() => {
						const milliseconds = timestamp < 10_000_000_000 ? timestamp * 1000 : timestamp;
						const parsedDate = TimestampService.getCurrentDate();
						parsedDate.setTime(milliseconds);
						return parsedDate;
					})();

		return TimestampService.formatForDisplay(date, {
			style: 'medium',
			locale: 'en-US',
		});
	};

	// Loading state
	if (isLoadingMerchants) {
		return (
			<div className='flex items-center justify-center min-h-screen'>
				<div className='text-center'>
					<div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4'></div>
					<p className='text-gray-600'>{t('merchant.redemption.loadingMerchant')}</p>
				</div>
			</div>
		);
	}

	if (selectedMerchant === null) {
		return (
			<div className='flex items-center justify-center min-h-screen'>
				<div className='text-center'>
					<h2 className='text-2xl font-bold text-gray-900 mb-4'>
						{t('merchant.redemption.noMerchantTitle')}
					</h2>
					<p className='text-gray-600'>{t('merchant.redemption.noMerchantMessage')}</p>
				</div>
			</div>
		);
	}

	return (
		<div className='space-y-4 sm:space-y-6 p-4 sm:p-6'>
			{/* Header */}
			<div className='bg-white rounded-xl shadow-sm border p-4 sm:p-6'>
				<div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
					<div className='flex items-center gap-3'>
						<div className='w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center'>
							<Clock className='w-6 h-6 text-blue-600' />
						</div>
						<div>
							<h1 className='text-xl sm:text-2xl font-bold text-gray-900'>
								{t('merchant.redemptionHistory.title')}
							</h1>
							<p className='text-gray-600 mt-1 text-sm sm:text-base'>
								{t('merchant.redemptionHistory.subtitle', {
									merchantName: selectedMerchant.merchantName,
								})}
							</p>
						</div>
					</div>

					{/* Refresh button */}
					<button
						onClick={() => window.location.reload()}
						disabled={isLoadingHistory}
						className='flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
					>
						<RefreshCw className={`w-4 h-4 ${isLoadingHistory ? 'animate-spin' : ''}`} />
						<span>{isLoadingHistory ? t('common.refreshing') : t('common.refresh')}</span>
					</button>
				</div>
			</div>

			{/* Statistics Cards */}
			<div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6'>
				<div className='bg-white rounded-xl shadow-sm border p-4 sm:p-6'>
					<div className='flex items-center'>
						<div className='flex-shrink-0'>
							<div className='w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center'>
								<Receipt className='w-6 h-6 text-blue-600' />
							</div>
						</div>
						<div className='ml-4'>
							<p className='text-xs sm:text-sm font-medium text-gray-600'>
								{t('merchant.redemption.statistics.totalRedemptions')}
							</p>
							<p className='text-xl sm:text-2xl font-bold text-gray-900'>
								{stats?.totalRedemptions || 0}
							</p>
						</div>
					</div>
				</div>

				<div className='bg-white rounded-xl shadow-sm border p-4 sm:p-6'>
					<div className='flex items-center'>
						<div className='flex-shrink-0'>
							<div className='w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center'>
								<DollarSign className='w-6 h-6 text-green-600' />
							</div>
						</div>
						<div className='ml-4'>
							<p className='text-xs sm:text-sm font-medium text-gray-600'>
								{t('merchant.redemption.statistics.totalDiscounts')}
							</p>
							<p className='text-xl sm:text-2xl font-bold text-gray-900'>
								{formatCurrency(stats?.totalDiscounts || 0)}
							</p>
						</div>
					</div>
				</div>

				<div className='bg-white rounded-xl shadow-sm border p-4 sm:p-6'>
					<div className='flex items-center'>
						<div className='flex-shrink-0'>
							<div className='w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center'>
								<TrendingUp className='w-6 h-6 text-purple-600' />
							</div>
						</div>
						<div className='ml-4'>
							<p className='text-xs sm:text-sm font-medium text-gray-600'>
								{t('merchant.redemption.statistics.totalRevenue')}
							</p>
							<p className='text-xl sm:text-2xl font-bold text-gray-900'>
								{formatCurrency(stats?.totalRevenue || 0)}
							</p>
						</div>
					</div>
				</div>

				<div className='bg-white rounded-xl shadow-sm border p-4 sm:p-6'>
					<div className='flex items-center'>
						<div className='flex-shrink-0'>
							<div className='w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center'>
								<Calendar className='w-6 h-6 text-orange-600' />
							</div>
						</div>
						<div className='ml-4'>
							<p className='text-xs sm:text-sm font-medium text-gray-600'>
								{t('merchant.redemptionHistory.statistics.thisMonth')}
							</p>
							<p className='text-xl sm:text-2xl font-bold text-gray-900'>
								{stats?.totalRedemptions || 0}
							</p>
						</div>
					</div>
				</div>
			</div>

			{/* History Table */}
			<div className='bg-white rounded-xl shadow-sm border p-4 sm:p-6'>
				<h2 className='text-base sm:text-lg font-semibold text-gray-900 mb-4'>
					{t('merchant.redemption.history.title')}
				</h2>

				{/* Error Display */}
				{historyError && (
					<div className='bg-red-50 border border-red-200 rounded-lg p-4 mb-4'>
						<div className='flex'>
							<div className='flex-shrink-0'>
								<svg className='h-5 w-5 text-red-400' viewBox='0 0 20 20' fill='currentColor'>
									<path
										fillRule='evenodd'
										d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
										clipRule='evenodd'
									/>
								</svg>
							</div>
							<div className='ml-3'>
								<h3 className='text-sm font-medium text-red-800'>
									{t('merchant.redemption.history.errorLoading')}
								</h3>
								<p className='text-sm text-red-700 mt-1'>{historyError}</p>
							</div>
						</div>
					</div>
				)}

				{/* History Table */}
				{isLoadingHistory ? (
					<div className='p-6 sm:p-8 text-center'>
						<div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4'></div>
						<p className='text-gray-600 text-sm sm:text-base'>{t('merchant.redemption.history.loading')}</p>
					</div>
					) : redemptions.length === 0 ? (
					<div className='p-6 sm:p-8 text-center'>
						<p className='text-gray-600 text-sm sm:text-base'>
							{t('merchant.redemption.history.noRedemptions')}
						</p>
					</div>
				) : (
					<>
						{/* Desktop Table View */}
						<div className='hidden lg:block overflow-x-auto'>
							<table className='w-full'>
								<thead className='bg-gray-50'>
									<tr>
										<th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
											{t('merchant.redemption.history.table.date')}
										</th>
										<th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
											{t('merchant.redemption.history.table.coupon')}
										</th>
										<th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
											{t('merchant.redemption.history.table.billAmount')}
										</th>
										<th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
											{t('merchant.redemption.history.table.discount')}
										</th>
										<th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
											{t('merchant.redemption.history.table.finalAmount')}
										</th>
										<th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
											{t('merchant.redemption.history.table.invoiceId')}
										</th>
										<th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
											{t('merchant.redemption.history.table.notes')}
										</th>
									</tr>
								</thead>
								<tbody className='bg-white divide-y divide-gray-200'>
										{redemptions.map(redemption => (
											<tr key={redemption.redemptionId} className='hover:bg-gray-50'>
												<td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900'>
													{formatDate(redemption.redeemedAt)}
												</td>
												<td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900'>
													<Link
														to={`/merchant/coupons/${redemption.couponId}`}
														className='block max-w-[18rem] truncate text-blue-600 hover:text-blue-700 hover:underline'
														title={redemption.couponName}
													>
														{redemption.couponName}
													</Link>
												</td>
												<td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900'>
													{formatCurrency(redemption.billAmount || 0)}
												</td>
												<td className='px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium'>
													{formatCurrency(redemption.discountAmount || 0)}
												</td>
												<td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium'>
													{formatCurrency(redemption.finalAmount || 0)}
												</td>
												<td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
													{redemption.invoiceId || '-'}
												</td>
												<td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
													{redemption.redemptionNotes || '-'}
												</td>
											</tr>
										))}
								</tbody>
							</table>
						</div>

						{/* Mobile Card View */}
						<div className='lg:hidden divide-y divide-gray-200'>
								{redemptions.map(redemption => (
									<div
										key={redemption.redemptionId}
										className='p-4 hover:bg-gray-50 transition-colors'
									>
										<div className='space-y-3'>
											{/* Header with Date and Coupon ID */}
											<div className='flex items-start justify-between'>
												<div>
													<Link
														to={`/merchant/coupons/${redemption.couponId}`}
														className='block max-w-[14rem] truncate text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline'
														title={redemption.couponName}
													>
														{redemption.couponName}
													</Link>
													<p className='text-xs text-gray-500 mt-1'>
														{formatDate(redemption.redeemedAt)}
													</p>
												</div>
												<span className='text-xs text-gray-500'>
													{t('merchant.memberList.table.idPrefix')}
													{redemption.redemptionId}
												</span>
											</div>

											{/* Financial Details */}
											<div className='grid grid-cols-2 gap-4 text-sm'>
												<div>
													<p className='text-gray-500'>
														{t('merchant.redemption.history.table.billAmount')}
													</p>
													<p className='font-medium text-gray-900'>
														{formatCurrency(redemption.billAmount || 0)}
													</p>
												</div>
												<div>
													<p className='text-gray-500'>
														{t('merchant.redemption.history.table.discount')}
													</p>
													<p className='font-medium text-green-600'>
														{formatCurrency(redemption.discountAmount || 0)}
													</p>
												</div>
											</div>

											<div className='grid grid-cols-2 gap-4 text-sm'>
												<div>
													<p className='text-gray-500'>
														{t('merchant.redemption.history.table.finalAmount')}
													</p>
													<p className='font-bold text-gray-900'>
														{formatCurrency(redemption.finalAmount || 0)}
													</p>
												</div>
												<div>
													<p className='text-gray-500'>
														{t('merchant.redemption.history.table.invoiceId')}
													</p>
													<p className='text-gray-700'>
														{redemption.invoiceId || '-'}
													</p>
												</div>
											</div>

											{/* Notes section if there are notes */}
											{redemption.redemptionNotes && (
												<div className='text-sm'>
													<p className='text-gray-500'>
														{t('merchant.redemption.history.table.notes')}
													</p>
													<p className='text-gray-700 text-xs mt-1'>
														{redemption.redemptionNotes}
													</p>
												</div>
											)}
										</div>
									</div>
								))}
						</div>
					</>
				)}
			</div>
		</div>
	);
};

export default RedemptionHistory;
